import React, { useState, useRef, useEffect } from 'react'
import { AppButton } from 'components/Common/AppButton/AppButton.component'

interface AppTextToSpeechProps {
  text: string
  disabled?: boolean
  autoPlay?: boolean
  voice?: string
  rate?: number
  pitch?: number
  className?: string
  showControls?: boolean
  onStart?: () => void
  onEnd?: () => void
  onError?: (error: string) => void
}

export const AppTextToSpeech: React.FC<AppTextToSpeechProps> = ({
  text,
  disabled = false,
  autoPlay = false,
  voice,
  rate = 1,
  pitch = 1,
  className = '',
  showControls = true,
  onStart,
  onEnd,
  onError,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [isSupported, setIsSupported] = useState(false)
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)

  // Check for speech synthesis support
  useEffect(() => {
    if ('speechSynthesis' in window) {
      setIsSupported(true)
      
      // Load voices
      const loadVoices = () => {
        const availableVoices = speechSynthesis.getVoices()
        setVoices(availableVoices)
      }
      
      loadVoices()
      speechSynthesis.onvoiceschanged = loadVoices
    }
  }, [])

  // Auto play effect
  useEffect(() => {
    if (autoPlay && text && isSupported && !disabled) {
      speak()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, autoPlay, isSupported, disabled])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel()
      }
    }
  }, [])

  const speak = () => {
    if (!isSupported || !text || disabled) return

    // Cancel any ongoing speech
    speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(text)
    utteranceRef.current = utterance

    // Set voice if specified
    if (voice) {
      const selectedVoice = voices.find(v => v.name === voice || v.lang.includes(voice))
      if (selectedVoice) {
        utterance.voice = selectedVoice
      }
    } else {
      // Try to find an English voice
      const englishVoice = voices.find(v => v.lang.startsWith('en'))
      if (englishVoice) {
        utterance.voice = englishVoice
      }
    }

    utterance.rate = rate
    utterance.pitch = pitch

    utterance.onstart = () => {
      setIsPlaying(true)
      setIsPaused(false)
      onStart?.()
    }

    utterance.onend = () => {
      setIsPlaying(false)
      setIsPaused(false)
      onEnd?.()
    }

    utterance.onerror = (event) => {
      setIsPlaying(false)
      setIsPaused(false)
      onError?.(`Speech synthesis error: ${event.error}`)
    }

    utterance.onpause = () => {
      setIsPaused(true)
    }

    utterance.onresume = () => {
      setIsPaused(false)
    }

    speechSynthesis.speak(utterance)
  }

  const pause = () => {
    if (speechSynthesis.speaking && !speechSynthesis.paused) {
      speechSynthesis.pause()
    }
  }

  const resume = () => {
    if (speechSynthesis.paused) {
      speechSynthesis.resume()
    }
  }

  const stop = () => {
    speechSynthesis.cancel()
    setIsPlaying(false)
    setIsPaused(false)
  }

  if (!isSupported) {
    return null
  }

  if (!showControls) {
    return null
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {!isPlaying ? (
        <AppButton
          variant="clear"
          size="sm"
          disabled={disabled || !text}
          onClick={speak}
          RightIcon={() => (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                clipRule="evenodd"
              />
            </svg>
          )}
        />
      ) : (
        <div className="flex items-center gap-1">
          {!isPaused ? (
            <AppButton
              variant="clear"
              size="sm"
              onClick={pause}
              RightIcon={() => (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            />
          ) : (
            <AppButton
              variant="clear"
              size="sm"
              onClick={resume}
              RightIcon={() => (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            />
          )}
          
          <AppButton
            variant="clear"
            size="sm"
            onClick={stop}
            RightIcon={() => (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          />
        </div>
      )}
    </div>
  )
}

// Hook for text-to-speech functionality
export const useTextToSpeech = () => {
  const [isSupported, setIsSupported] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])

  useEffect(() => {
    if ('speechSynthesis' in window) {
      setIsSupported(true)
      
      const loadVoices = () => {
        setVoices(speechSynthesis.getVoices())
      }
      
      loadVoices()
      speechSynthesis.onvoiceschanged = loadVoices
    }
  }, [])

  const speak = (text: string, options?: {
    voice?: string
    rate?: number
    pitch?: number
    onStart?: () => void
    onEnd?: () => void
    onError?: (error: string) => void
  }) => {
    if (!isSupported || !text) return

    speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(text)
    
    if (options?.voice) {
      const selectedVoice = voices.find(v => v.name === options.voice || v.lang.includes(options.voice || ''))
      if (selectedVoice) {
        utterance.voice = selectedVoice
      }
    }

    utterance.rate = options?.rate || 1
    utterance.pitch = options?.pitch || 1

    utterance.onstart = () => {
      setIsPlaying(true)
      options?.onStart?.()
    }

    utterance.onend = () => {
      setIsPlaying(false)
      options?.onEnd?.()
    }

    utterance.onerror = (event) => {
      setIsPlaying(false)
      options?.onError?.(`Speech synthesis error: ${event.error}`)
    }

    speechSynthesis.speak(utterance)
  }

  const stop = () => {
    speechSynthesis.cancel()
    setIsPlaying(false)
  }

  return {
    isSupported,
    isPlaying,
    voices,
    speak,
    stop,
  }
}
